import Decimal from 'decimal.js';
import { buildMetricValueCsv } from './csvExport';
import type {
  DisposalDetail,
  FifoResult,
  LedgerEvent,
  Lot,
  OwnedWallet,
} from './types';

const AMT_EPS = new Decimal('1e-18');

/** Rows without chain addresses (e.g. Crypto.com only) share one FIFO pool. */
export const OMNIBUS_WALLET_KEY = '__omnibus__';

export type WalletContinuityFifoStats = {
  /** INTERNAL_TRANSFER / SELF_TRANSFER rows between two owned addresses. */
  ownedToOwnedInternalRows: number;
  /** Successful lot moves (slices) across wallets. */
  lotMovesSucceeded: number;
  /** Moves skipped (missing from/to or empty source inventory). */
  lotMovesSkipped: number;
  /** Same `ref`: duplicate TRANSFER_IN acquisition removed (grouped-event hygiene). */
  sameTxSpuriousTransferInSuppressed: number;
  /** Disposal legs that took inventory from `__omnibus__` after wallet pool was exhausted. */
  disposalOmnibusFallbackLegs: number;
  /** Synthetic CDC omnibus → on-chain wallet continuity (`CROSS_ENV_CDC_OMNIBUS_TO_CHAIN`). */
  crossEnvOmnibusToWalletMoves?: number;
};

export type FifoResultWithWalletContinuity = FifoResult & {
  walletContinuity: WalletContinuityFifoStats;
};

function sortLots(lots: Lot[]): Lot[] {
  return [...lots].sort((a, b) => {
    if (a.acquiredAt !== b.acquiredAt) return a.acquiredAt - b.acquiredAt;
    return String(a.lotId ?? '').localeCompare(String(b.lotId ?? ''));
  });
}

function inventoryKey(assetUpper: string, walletKey: string): string {
  return `${assetUpper}::${walletKey}`;
}

function normalizeAddr(a: string | undefined): string | null {
  if (!a) return null;
  const t = a.trim().toLowerCase();
  return t.startsWith('0x') && t.length === 42 ? t : null;
}

function walletKeyForAcquisition(ev: LedgerEvent): string {
  return (
    normalizeAddr(ev.primaryWalletAddress) ??
    normalizeAddr(ev.toAddress) ??
    OMNIBUS_WALLET_KEY
  );
}

function walletKeyForDisposal(ev: LedgerEvent): string {
  return (
    normalizeAddr(ev.primaryWalletAddress) ??
    normalizeAddr(ev.fromAddress) ??
    OMNIBUS_WALLET_KEY
  );
}

function isNonTaxableInternal(ev: LedgerEvent): boolean {
  return (
    ev.kind === 'internal_transfer' &&
    (ev.fiTaxCategory === 'NON_TAXABLE_INTERNAL' ||
      ev.classification === 'INTERNAL_TRANSFER' ||
      ev.classification === 'SELF_TRANSFER')
  );
}

function isOwnedToOwnedInternal(ev: LedgerEvent, owned: Set<string>): boolean {
  if (!isNonTaxableInternal(ev)) return false;
  const f = normalizeAddr(ev.fromAddress);
  const t = normalizeAddr(ev.toAddress);
  if (!f || !t || f === t) return false;
  return owned.has(f) && owned.has(t);
}

let lotSeq = 0;
function makeGenId(): () => string {
  lotSeq = 0;
  return () => {
    lotSeq += 1;
    return `lot_${lotSeq}`;
  };
}

/**
 * When the same tx `ref` contains both an owned-to-owned internal leg and a duplicate
 * `acquisition` TRANSFER_IN of the same asset/amount, drop the acquisition (phantom inventory).
 * Does not remove legs when a taxable `disposal` exists in the group (swap / sell).
 */
export function suppressSpuriousTransferInAfterInternal(
  events: LedgerEvent[],
  owned: Set<string>
): { events: LedgerEvent[]; suppressed: number } {
  const byRef = new Map<string, LedgerEvent[]>();
  for (const ev of events) {
    const r = ev.ref?.trim().toLowerCase();
    if (!r) continue;
    const arr = byRef.get(r) ?? [];
    arr.push(ev);
    byRef.set(r, arr);
  }

  const dropIndices = new Set<number>();
  let suppressed = 0;

  for (const [, group] of Array.from(byRef.entries())) {
    const hasDisposal = group.some((e) => e.kind === 'disposal');
    if (hasDisposal) continue;

    const internals = group.filter((e) => isOwnedToOwnedInternal(e, owned));
    if (internals.length === 0) continue;

    for (const ev of group) {
      if (ev.kind !== 'acquisition') continue;
      if (ev.classification != null && ev.classification !== 'TRANSFER_IN') continue;

      const asset = ev.asset.toUpperCase();
      const amt = ev.amount;
      const match = internals.some((it) => {
        if (it.asset.toUpperCase() !== asset) return false;
        return it.amount.sub(amt).abs().lte(AMT_EPS);
      });
      if (!match) continue;

      const idx = events.indexOf(ev);
      if (idx < 0) continue;
      dropIndices.add(idx);
      suppressed++;
    }
  }

  if (dropIndices.size === 0) return { events, suppressed: 0 };

  const out = events.filter((_, i) => !dropIndices.has(i));
  return { events: out, suppressed };
}

/**
 * Consume disposal `remainingIn` from a FIFO `queue` (mutates queue). Returns unmatched remainder and gain/loss slices.
 */
function consumeDisposalFromLotQueue(
  queue: Lot[],
  queueLabel: 'wallet' | 'omnibus',
  ev: LedgerEvent,
  asset: string,
  wDisposal: string,
  stats: WalletContinuityFifoStats,
  details: DisposalDetail[],
  remainingIn: Decimal
): { remaining: Decimal; gainSlice: Decimal; lossSlice: Decimal } {
  let remaining = remainingIn;
  let gainSlice = new Decimal(0);
  let lossSlice = new Decimal(0);

  while (remaining.gt(AMT_EPS) && queue.length > 0) {
    const lot = queue[0]!;
    const take = Decimal.min(remaining, lot.amount);
    const costPortion = lot.costEur.mul(take).div(lot.amount);
    const proceedsPortion = ev.eurValue!.mul(take).div(ev.amount);
    lot.amount = lot.amount.minus(take);
    lot.costEur = lot.costEur.minus(costPortion);
    remaining = remaining.minus(take);

    const gain = proceedsPortion.minus(costPortion);
    if (gain.gte(0)) gainSlice = gainSlice.plus(gain);
    else lossSlice = lossSlice.plus(gain.abs());

    if (queueLabel === 'omnibus' && wDisposal !== OMNIBUS_WALLET_KEY) {
      stats.disposalOmnibusFallbackLegs++;
    }

    details.push({
      asset,
      ts: ev.ts,
      disposed: take,
      proceedsEur: proceedsPortion,
      costEur: costPortion,
      gainEur: gain,
      source: ev.source,
      ref: ev.ref,
      lotAcquiredAt: lot.acquiredAt,
      csvMeta: ev.csvMeta,
      proceedsDerivation: ev.proceedsDerivation,
      valuationFlag: ev.valuationFlag,
      lotBasisRepair: lot.lotBasisRepair,
      acquisitionProvenance: lot.acquisitionProvenance,
      acquisitionProvenanceFlag: lot.acquisitionProvenanceFlag,
      lotId: lot.lotId,
      originLotId: lot.originLotId,
      originTimestamp: lot.originTimestamp,
      originSourceRef: lot.originSourceRef,
      originWallet: lot.originWallet,
      currentWallet: lot.currentWallet,
      basisContinuityRef: lot.basisContinuityRef,
      walletContinuityKind: lot.walletContinuityKind,
    });

    if (lot.amount.lte(AMT_EPS)) queue.shift();
  }

  return { remaining, gainSlice, lossSlice };
}

function moveLotsBetweenWallets(
  inv: Map<string, Lot[]>,
  assetUpper: string,
  fromW: string,
  toW: string,
  amount: Decimal,
  genId: () => string,
  stats: WalletContinuityFifoStats
): void {
  const fromKey = inventoryKey(assetUpper, fromW);
  const toKey = inventoryKey(assetUpper, toW);
  let src = sortLots(inv.get(fromKey) ?? []);
  const dest = sortLots(inv.get(toKey) ?? []);

  let remaining = amount;
  if (remaining.lte(AMT_EPS)) return;
  if (src.length === 0) {
    stats.lotMovesSkipped++;
    return;
  }

  while (remaining.gt(AMT_EPS) && src.length > 0) {
    const head = src[0]!;
    const take = Decimal.min(remaining, head.amount);
    const costTake = head.costEur.mul(take).div(head.amount);

    if (take.eq(head.amount)) {
      src.shift();
    } else {
      head.amount = head.amount.minus(take);
      head.costEur = head.costEur.minus(costTake);
    }

    const moved: Lot = {
      ...head,
      lotId: genId(),
      amount: take,
      costEur: costTake,
      basisContinuityRef: head.lotId,
      walletContinuityKind: 'owned_wallet_continuation',
      currentWallet: toW,
      originLotId: head.originLotId ?? head.lotId,
      originTimestamp: head.originTimestamp ?? head.acquiredAt,
      originSourceRef: head.originSourceRef,
      originWallet: head.originWallet ?? head.currentWallet ?? fromW,
      acquiredAt: head.acquiredAt,
    };
    dest.push(moved);
    remaining = remaining.minus(take);
    stats.lotMovesSucceeded++;
  }

  inv.set(fromKey, sortLots(src));
  inv.set(toKey, sortLots(dest));
}

/**
 * FIFO with per–wallet-address inventory pools + omnibus for rows without addresses.
 * Owned-wallet internal transfers move lot slices (proportional basis, original acquisition date).
 */
export function runFifoWithOwnedWalletContinuity(
  events: LedgerEvent[],
  options: { ownedAddresses: Set<string> }
): FifoResultWithWalletContinuity {
  const owned = options.ownedAddresses;
  const genId = makeGenId();
  const stats: WalletContinuityFifoStats = {
    ownedToOwnedInternalRows: 0,
    lotMovesSucceeded: 0,
    lotMovesSkipped: 0,
    sameTxSpuriousTransferInSuppressed: 0,
    disposalOmnibusFallbackLegs: 0,
    crossEnvOmnibusToWalletMoves: 0,
  };

  const sorted = events
    .map((ev, i) => ({ ev, i }))
    .sort((a, b) => {
      if (a.ev.ts !== b.ev.ts) return a.ev.ts - b.ev.ts;
      const ra = a.ev.ref ?? '';
      const rb = b.ev.ref ?? '';
      if (ra !== rb) return ra.localeCompare(rb);
      return a.i - b.i;
    })
    .map((x) => x.ev);

  const inv = new Map<string, Lot[]>();
  const warnings: string[] = [];
  const details: DisposalDetail[] = [];
  let totalGainEur = new Decimal(0);
  let totalLossEur = new Decimal(0);

  for (const ev of sorted) {
    if (ev.classification === 'IGNORE_NON_TAX') continue;
    if (ev.kind === 'internal_transfer') {
      if (
        ev.fiSubreason === 'CROSS_ENV_CDC_OMNIBUS_TO_CHAIN' &&
        ev.fromAddress === OMNIBUS_WALLET_KEY
      ) {
        const t = normalizeAddr(ev.toAddress);
        if (t && owned.has(t)) {
          moveLotsBetweenWallets(
            inv,
            ev.asset.toUpperCase(),
            OMNIBUS_WALLET_KEY,
            t,
            ev.amount,
            genId,
            stats
          );
          stats.crossEnvOmnibusToWalletMoves =
            (stats.crossEnvOmnibusToWalletMoves ?? 0) + 1;
        }
        continue;
      }
      if (isOwnedToOwnedInternal(ev, owned)) {
        stats.ownedToOwnedInternalRows++;
        const f = normalizeAddr(ev.fromAddress)!;
        const t = normalizeAddr(ev.toAddress)!;
        moveLotsBetweenWallets(inv, ev.asset.toUpperCase(), f, t, ev.amount, genId, stats);
      }
      continue;
    }

    if (ev.classification === 'UNKNOWN_REVIEW' && ev.eurValue == null) continue;
    if (ev.eurValue == null) {
      warnings.push(
        `Skipped ${ev.source} ${ev.kind} ${ev.asset} (${new Date(ev.ts).toISOString().slice(0, 10)}) — no EUR value yet${ev.ref ? ` ref ${ev.ref.slice(0, 14)}…` : ''}.`
      );
      continue;
    }

    const asset = ev.asset.toUpperCase();
    const wDisposal = walletKeyForDisposal(ev);
    const wAcq = walletKeyForAcquisition(ev);

    if (ev.kind === 'acquisition' || ev.kind === 'income') {
      const list = inv.get(inventoryKey(asset, wAcq)) ?? [];
      const id = genId();
      list.push({
        asset,
        amount: ev.amount,
        costEur: ev.eurValue!,
        acquiredAt: ev.ts,
        lotId: id,
        originLotId: id,
        originTimestamp: ev.ts,
        originSourceRef: ev.ref,
        originWallet: wAcq,
        currentWallet: wAcq,
        walletContinuityKind: 'economic_acquisition',
        lotBasisRepair: ev.lotBasisRepair,
        acquisitionProvenance: ev.acquisitionProvenance,
        acquisitionProvenanceFlag: ev.acquisitionProvenanceFlag,
      });
      inv.set(inventoryKey(asset, wAcq), sortLots(list));
      continue;
    }

    let remaining = ev.amount;
    const wKey = inventoryKey(asset, wDisposal);
    const oKey = inventoryKey(asset, OMNIBUS_WALLET_KEY);

    let gainAcc = new Decimal(0);
    let lossAcc = new Decimal(0);

    if (wDisposal === OMNIBUS_WALLET_KEY) {
      let pool = sortLots(inv.get(oKey) ?? []);
      const r = consumeDisposalFromLotQueue(
        pool,
        'wallet',
        ev,
        asset,
        wDisposal,
        stats,
        details,
        remaining
      );
      remaining = r.remaining;
      gainAcc = gainAcc.plus(r.gainSlice);
      lossAcc = lossAcc.plus(r.lossSlice);
      inv.set(oKey, sortLots(pool));
    } else {
      let walletLots = sortLots(inv.get(wKey) ?? []);
      let omniLots = sortLots(inv.get(oKey) ?? []);
      const r1 = consumeDisposalFromLotQueue(
        walletLots,
        'wallet',
        ev,
        asset,
        wDisposal,
        stats,
        details,
        remaining
      );
      remaining = r1.remaining;
      gainAcc = gainAcc.plus(r1.gainSlice);
      lossAcc = lossAcc.plus(r1.lossSlice);
      if (remaining.gt(AMT_EPS)) {
        const r2 = consumeDisposalFromLotQueue(
          omniLots,
          'omnibus',
          ev,
          asset,
          wDisposal,
          stats,
          details,
          remaining
        );
        remaining = r2.remaining;
        gainAcc = gainAcc.plus(r2.gainSlice);
        lossAcc = lossAcc.plus(r2.lossSlice);
      }
      inv.set(wKey, sortLots(walletLots));
      inv.set(oKey, sortLots(omniLots));
    }

    totalGainEur = totalGainEur.plus(gainAcc);
    totalLossEur = totalLossEur.plus(lossAcc);

    if (remaining.gt(AMT_EPS)) {
      const proceedsPortion = ev.eurValue.mul(remaining).div(ev.amount);
      warnings.push(
        `Insufficient ${asset} lots; ${remaining.toString()} units with ~€${proceedsPortion.toFixed(2)} proceeds (cost basis 0).`
      );
      if (proceedsPortion.gte(0)) totalGainEur = totalGainEur.plus(proceedsPortion);
      else totalLossEur = totalLossEur.plus(proceedsPortion.abs());
      details.push({
        asset,
        ts: ev.ts,
        disposed: remaining,
        proceedsEur: proceedsPortion,
        costEur: new Decimal(0),
        gainEur: proceedsPortion,
        source: ev.source,
        ref: ev.ref,
        csvMeta: ev.csvMeta,
        proceedsDerivation: ev.proceedsDerivation,
        valuationFlag: ev.valuationFlag,
        lotBasisRepair: undefined,
      });
    }
  }

  return {
    details,
    totalGainEur,
    totalLossEur,
    warnings,
    walletContinuity: stats,
  };
}

/** Drop continuity stats for APIs expecting plain `FifoResult`. */
export function fifoResultWithoutWalletContinuity(
  r: FifoResultWithWalletContinuity
): FifoResult {
  const { walletContinuity: _w, ...rest } = r;
  return rest;
}

/** Build owned address set from UI wallet list (empty → caller should use legacy `runFifo`). */
export function ownedAddressSetFromWallets(wallets: OwnedWallet[]): Set<string> {
  return new Set(wallets.map((w) => w.address.toLowerCase()));
}

/**
 * Apply same-tx suppression + return events for FIFO. Idempotent.
 */
export function applyWalletContinuityLedgerPrep(
  events: LedgerEvent[],
  wallets: OwnedWallet[]
): { events: LedgerEvent[]; sameTxSpuriousTransferInSuppressed: number } {
  const owned = ownedAddressSetFromWallets(wallets);
  if (owned.size === 0) return { events, sameTxSpuriousTransferInSuppressed: 0 };
  const r = suppressSpuriousTransferInAfterInternal(events, owned);
  return { events: r.events, sameTxSpuriousTransferInSuppressed: r.suppressed };
}

export function buildWalletContinuityDiagnosticsCsv(
  stats: WalletContinuityFifoStats & {
    fifoGainDeltaVsGlobalEur?: string;
    disposalUnknownProvRemaining?: number;
  }
): string {
  const rows: string[][] = [
    ['owned_to_owned_internal_rows', String(stats.ownedToOwnedInternalRows)],
    ['lot_moves_succeeded', String(stats.lotMovesSucceeded)],
    ['lot_moves_skipped', String(stats.lotMovesSkipped)],
    ['same_tx_spurious_transfer_in_suppressed', String(stats.sameTxSpuriousTransferInSuppressed)],
    ['disposal_omnibus_fallback_legs', String(stats.disposalOmnibusFallbackLegs)],
  ];
  if (stats.fifoGainDeltaVsGlobalEur != null) {
    rows.push(['fifo_gain_delta_vs_global_eur', stats.fifoGainDeltaVsGlobalEur]);
  }
  if (stats.disposalUnknownProvRemaining != null) {
    rows.push(['disposal_unknown_provenance_remaining', String(stats.disposalUnknownProvRemaining)]);
  }
  return buildMetricValueCsv(rows);
}
